import * as React from "react";
import { Info, Lightbulb, Sparkles, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Checkbox, Dialog } from "@/components/ui";
import { HelpCallout, HelpDialog } from "@/containers/Coach/DesignStudio/help/HelpDialog";
import { cn } from "@/lib/utils";
import { QuoteEdit } from "@/services/bedrock/dtos/DesignRenderGroup";
import { Review } from "@/services/bedrock/dtos/Review";
import { ReviewServices } from "@/services/bedrock/ReviewServices";
import { plural } from "../ReviewPicker";
import { reviewSelection, quoteSelection, SelectionInElement } from "./quoteSelection";
import {
  allowedPhraseChange,
  canToggle,
  coverage,
  ELLIPSIS,
  locateRanges,
  MAX_QUOTE_LENGTH,
  mergeRanges,
  phrasesFromSelection,
  quoteFromRanges,
  quoteLength,
  reviewText,
  sameQuote,
  sanitizeQuote,
  selectionFromPhrases,
  sentenceSpans,
  subtractRange,
  TextRange,
  tokenize,
} from "./quoteText";

export interface QuoteEditorDialogProps {
  /** The review being edited; the dialog is closed while this is undefined. */
  review: Review | undefined;
  /** This post's current edit for the review, if any. */
  edit?: QuoteEdit;
  onClose: () => void;
  /** The post's new quote for the review, or null when it's the review's own again. */
  onDone: (edit: QuoteEdit | null) => void;
  /** A coach posting for a player may also make the quote the review's own. */
  canSaveToReview?: boolean;
}

const GAP_MESSAGE = "We didn't add that. A quote can skip over two parts of the review at most.";
const LENGTH_MESSAGE = "That's too long for the image. Take a sentence out, or select fewer words.";
const PHRASE_LIMIT_MESSAGE = "We didn't highlight that. You can highlight two phrases, up to six words each.";
const SUGGEST_FAILED = "We couldn't suggest highlights. Tap words to pick your own.";
const SAVE_TO_REVIEW_FAILED = "We couldn't update the review. Your post still uses this quote.";
// Lets a drag settle before the button beside it moves.
const SELECTION_SETTLE_MS = 120;
// Room the button needs above a selection before it goes below instead.
const BAR_ROOM = 44;

/**
 * Selectable text whose selection reads by lightness, not hue: dark with white letters, so it never looks
 * like the light blue of a picked sentence or the amber of a highlight, color-blind or not.
 */
const SELECTABLE = "select-text selection:bg-slate-900 selection:text-white";

/**
 * The item the arrow keys are on, outlined only while the box has keyboard focus. The items themselves
 * are never focusable: a press on a focusable span moves focus to it, and when the press lands on the
 * first letter (so the selection starts just outside that span) Chrome then won't extend the drag.
 */
const REFUSED_ITEM = "outline-2 outline-offset-2 outline-transparent animate-refuse";

const ACTIVE_ITEM =
  "group-focus-visible/list:outline-2 group-focus-visible/list:outline-offset-2 group-focus-visible/list:outline-primary";

function pickedLine(full: number, part: number): string {
  if (full && part)
    return `${plural(full, "sentence")} picked, plus part of ${part === 1 ? "another" : `${part} more`}`;
  if (full) return `${plural(full, "sentence")} picked`;
  if (part) return part === 1 ? "Part of a sentence picked" : `Parts of ${part} sentences picked`;
  return "Nothing picked yet";
}

/** A tap that ends a drag over the words is a selection, not a tap. */
function selectingText(): boolean {
  const selection = typeof window !== "undefined" ? window.getSelection() : null;
  return !!selection && !selection.isCollapsed && selection.toString().trim().length > 0;
}

function clearSelection() {
  window.getSelection()?.removeAllRanges();
}

/**
 * Keys for a box of tappable items: arrows (and Home, End) move between them, Enter or Space toggles
 * the current one. The box is the only tab stop; see the note where the items are drawn.
 */
function listKeyDown(
  event: React.KeyboardEvent,
  items: number[],
  active: number,
  setActive: (i: number) => void,
  toggle: (i: number) => void
) {
  if (!items.length) return;
  const at = items.indexOf(active);
  const moveTo = (i: number) => {
    event.preventDefault();
    setActive(items[Math.max(0, Math.min(items.length - 1, i))]);
  };
  if (event.key === "ArrowRight" || event.key === "ArrowDown") moveTo(at + 1);
  else if (event.key === "ArrowLeft" || event.key === "ArrowUp") moveTo(at < 0 ? 0 : at - 1);
  else if (event.key === "Home") moveTo(0);
  else if (event.key === "End") moveTo(items.length - 1);
  else if ((event.key === "Enter" || event.key === " ") && at >= 0) {
    event.preventDefault();
    toggle(active);
  }
}

interface Bar {
  kind: "review" | "quote";
  label: string;
  top: number;
  left: number;
  below: boolean;
}

/** Above the selection so it never covers where the next drag starts; below only when there's no room. */
function barPlacement(wrap: HTMLElement | null, rect: SelectionInElement["rect"]) {
  const box = wrap?.getBoundingClientRect() ?? { top: 0, left: 0 };
  const above = rect.top - box.top;
  const below = above < BAR_ROOM;
  return { left: Math.max(0, rect.left - box.left), top: below ? rect.bottom - box.top + 8 : above - 6, below };
}

const SelectionBar: React.FC<{ bar: Bar; wrap: HTMLElement | null; onApply: () => void }> = ({
  bar,
  wrap,
  onApply,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [left, setLeft] = React.useState(bar.left);
  // Kept inside the box, measured once the button has its width.
  React.useLayoutEffect(() => {
    const width = ref.current?.offsetWidth ?? 0;
    const room = wrap?.clientWidth ?? 0;
    setLeft(room && width ? Math.max(0, Math.min(bar.left, room - width)) : bar.left);
  }, [bar.left, bar.label, wrap]);
  return (
    <div
      ref={ref}
      className="absolute z-10 flex"
      style={{ top: bar.top, left, transform: bar.below ? undefined : "translateY(-100%)" }}
      data-selection-bar
      // Keeps the selection alive through the click.
      onMouseDown={event => event.preventDefault()}
    >
      <Button size="sm" className="font-bold shadow-lg" onClick={onApply}>
        {bar.label}
      </Button>
    </div>
  );
};

type WhyTopic = "skips" | "length" | "highlights";

/** The help behind each limit's "Why?": the rule in a line, the reasons, and what to do instead. */
const WHY: Record<WhyTopic, { title: string; lead: string; reasons: string[]; tip: string }> = {
  skips: {
    title: "Why only two skips?",
    lead: 'Every skip adds a "…" to the quote.',
    reasons: [
      "One or two keep it easy to read. More makes it choppy.",
      "More than two can look like the words were rearranged, even when every word is the reviewer's.",
    ],
    tip: "If the parts you love are far apart, pick the strongest one.",
  },
  length: {
    title: "Why does the quote have to be short?",
    lead: "Each design fits about two or three short sentences.",
    reasons: [
      "A longer quote won't fit at a size people can read on a phone.",
      "People scroll past a block of text. One strong line stops them.",
    ],
    tip: "Not sure what to cut? Keep the part that says what you did for them.",
  },
  highlights: {
    title: "Why only two highlights?",
    lead: "Highlights work because they stand out.",
    reasons: [
      "One or two short phrases pull the eye to the best part of the review.",
      "Six words at most: a few words read at a glance, a whole sentence reads as a block.",
    ],
    tip: "To highlight something else, tap a highlighted word to remove it first.",
  },
};

const WhyDialog: React.FC<{ topic: WhyTopic | null; onClose: () => void }> = ({ topic, onClose }) => {
  // Kept while the dialog closes, so its text doesn't vanish mid-animation.
  const [shown, setShown] = React.useState<WhyTopic>(topic ?? "skips");
  React.useEffect(() => {
    if (topic) setShown(topic);
  }, [topic]);
  const { title, lead, reasons, tip } = WHY[shown];
  return (
    <HelpDialog
      open={!!topic}
      onOpenChange={open => !open && onClose()}
      title={title}
      size="md"
      showLogo={false}
      footer={<Button onClick={onClose}>Got it</Button>}
    >
      <div className="flex flex-col gap-3 text-[15px] leading-relaxed text-slate-700">
        <p className="m-0 font-semibold text-slate-800">{lead}</p>
        <ul className="m-0 flex list-disc flex-col gap-1.5 pl-5">
          {reasons.map(reason => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      </div>
      <HelpCallout icon={Lightbulb}>{tip}</HelpCallout>
    </HelpDialog>
  );
};

/**
 * A message under a box. An icon as well as a color tells an error (something to fix before Done) from a
 * refused tap (nothing changed), so the two read apart without color.
 */
const Notice = React.forwardRef<
  HTMLParagraphElement,
  { tone: "error" | "refused"; id?: string; children: React.ReactNode }
>(function Notice({ tone, id, children }, ref) {
  const Icon = tone === "error" ? TriangleAlert : Info;
  return (
    <p
      ref={ref}
      id={id}
      role="alert"
      className={cn(
        "m-0 flex items-start gap-1.5 text-[13px] font-semibold",
        tone === "error" ? "text-red-700" : "text-amber-800"
      )}
    >
      <Icon className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
});

const WhyLink: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Button variant="link" size="inline" className="text-[13px] font-bold" onClick={onClick}>
    Why?
  </Button>
);

interface Draft {
  ranges: TextRange[];
  quote: string;
  selected: Set<number>;
  gapRefused: boolean;
  phraseRefused: boolean;
  suggesting: boolean;
  suggestFailed: boolean;
}

const EditorBody: React.FC<Omit<QuoteEditorDialogProps, "review"> & { review: Review }> = ({
  review,
  edit,
  onClose,
  onDone,
  canSaveToReview,
}) => {
  const queryClient = useQueryClient();
  const h = React.useMemo(() => reviewText(review.content), [review.content]);
  const spans = React.useMemo(() => sentenceSpans(h), [h]);
  const stored = { quote: review.condensedQuote ?? "", phrases: review.condensedHighlightPhrases ?? [] };

  const draftFrom = (quote: string, phrases: string[]): Draft => {
    const clean = sanitizeQuote(quote);
    return {
      ranges: locateRanges(h, clean),
      quote: clean,
      selected: selectionFromPhrases(tokenize(clean), phrases),
      gapRefused: false,
      phraseRefused: false,
      suggesting: false,
      suggestFailed: false,
    };
  };
  const [draft, setDraft] = React.useState<Draft>(() =>
    edit ? draftFrom(edit.condensedQuote, edit.condensedHighlightPhrases) : draftFrom(stored.quote, stored.phrases)
  );
  const [saveToReview, setSaveToReview] = React.useState(false);
  const [why, setWhy] = React.useState<WhyTopic | null>(null);
  // The latest refused tap, so the item flashes and its message comes into view; n tells repeats apart.
  const [refusal, setRefusal] = React.useState<{ kind: "sentence" | "word"; index: number | null; n: number } | null>(
    null
  );
  const refuse = (kind: "sentence" | "word", index: number | null) =>
    setRefusal(r => ({ kind, index, n: (r?.n ?? 0) + 1 }));
  const gapMessageRef = React.useRef<HTMLParagraphElement>(null);
  const phraseMessageRef = React.useRef<HTMLParagraphElement>(null);
  React.useEffect(() => {
    if (!refusal) return undefined;
    const message = refusal.kind === "sentence" ? gapMessageRef.current : phraseMessageRef.current;
    // On a long review the message under the box can be off screen, out of reach of the finger that tapped.
    message?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    const timer = setTimeout(() => setRefusal(r => (r?.n === refusal.n ? { ...r, index: null } : r)), 700);
    return () => clearTimeout(timer);
  }, [refusal?.n]);
  const [bar, setBar] = React.useState<Bar | null>(null);

  const reviewRef = React.useRef<HTMLParagraphElement>(null);
  const reviewWrapRef = React.useRef<HTMLDivElement>(null);
  const quoteRef = React.useRef<HTMLDivElement>(null);
  const quoteWrapRef = React.useRef<HTMLDivElement>(null);

  const tokens = React.useMemo(() => tokenize(draft.quote), [draft.quote]);
  const cover = spans.map(span => coverage(h, span, draft.ranges));
  const draftRef = React.useRef({ draft, tokens, cover });
  draftRef.current = { draft, tokens, cover };

  const phrases = phrasesFromSelection(tokens, draft.selected);
  // The review's own quote is fine at any length; only a quote the person changed is held to the image's limit.
  const tooLong =
    quoteLength(draft.quote) > MAX_QUOTE_LENGTH && sanitizeQuote(draft.quote) !== sanitizeQuote(stored.quote);
  const atStored = sameQuote({ quote: draft.quote, phrases }, stored);
  // Suggestions are only written for quotes that fit the image, the review's own long quote included.
  const overLength = quoteLength(draft.quote) > MAX_QUOTE_LENGTH;
  const doneBlocked = !tokens.length ? "Pick a sentence first." : tooLong ? "Your quote is too long." : null;

  // ---- step 1: the quote ----

  const setRanges = (next: TextRange[]) =>
    setDraft(d => {
      const quote = quoteFromRanges(h, next);
      if (quote === null) return { ...d, gapRefused: true };
      // The highlights still in the new quote stay.
      const kept = phrasesFromSelection(tokenize(d.quote), d.selected);
      return {
        ...d,
        ranges: mergeRanges(h, next),
        quote,
        selected: selectionFromPhrases(tokenize(quote), kept),
        gapRefused: false,
        phraseRefused: false,
      };
    });

  const toggleSentence = (i: number) => {
    const { draft: d, cover: c } = draftRef.current;
    const next = c[i] === "on" ? subtractRange(h, d.ranges, spans[i]) : [...d.ranges, spans[i]];
    if (quoteFromRanges(h, next) === null) refuse("sentence", i);
    setRanges(next);
  };

  const touchedSentences = (range: TextRange) =>
    spans.map((span, i) => (span.start < range.end && span.end > range.start ? i : -1)).filter(i => i >= 0);

  // Inside sentences picked whole, the selection replaces their pick; otherwise it adds to the quote.
  const replacesPick = (range: TextRange) => {
    const touched = touchedSentences(range);
    return touched.length > 0 && touched.every(i => draftRef.current.cover[i] === "on");
  };

  const applyReviewSelection = () => {
    const selected = reviewSelection(reviewRef.current, h);
    setBar(null);
    if (!selected) return;
    clearSelection();
    let next = draftRef.current.draft.ranges;
    if (replacesPick(selected)) touchedSentences(selected).forEach(i => (next = subtractRange(h, next, spans[i])));
    next = [...next, { start: selected.start, end: selected.end }];
    if (quoteFromRanges(h, next) === null) refuse("sentence", null);
    setRanges(next);
  };

  // ---- step 2: the highlights ----

  const setSelected = (next: Set<number>) =>
    setDraft(d =>
      allowedPhraseChange(d.selected, next)
        ? { ...d, selected: next, phraseRefused: false, suggestFailed: false }
        : { ...d, phraseRefused: true }
    );

  const toggleWord = (i: number) => {
    const { draft: d, tokens: t } = draftRef.current;
    if (!canToggle(t, d.selected, i)) {
      setDraft(current => ({ ...current, phraseRefused: true }));
      refuse("word", i);
      return;
    }
    const next = new Set(d.selected);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    setSelected(next);
  };

  const applyQuoteSelection = () => {
    const { draft: d, tokens: t } = draftRef.current;
    const selected = quoteSelection(quoteRef.current, t);
    setBar(null);
    if (!selected) return;
    clearSelection();
    const removing = selected.indexes.every(i => d.selected.has(i));
    const next = new Set(d.selected);
    selected.indexes.forEach(i => (removing ? next.delete(i) : next.add(i)));
    if (!allowedPhraseChange(d.selected, next)) refuse("word", null);
    setSelected(next);
  };

  const suggest = async () => {
    const quote = draft.quote;
    setDraft(d => ({ ...d, suggesting: true, suggestFailed: false, phraseRefused: false }));
    try {
      const { condensedHighlightPhrases } = await ReviewServices.suggestHighlights(review.id, {
        condensedQuote: quote,
      });
      setDraft(d =>
        d.quote === quote
          ? { ...d, suggesting: false, selected: selectionFromPhrases(tokenize(quote), condensedHighlightPhrases) }
          : { ...d, suggesting: false }
      );
    } catch {
      setDraft(d => ({ ...d, suggesting: false, suggestFailed: true }));
    }
  };

  // ---- selections raise the button beside them ----

  React.useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onSelectionChange = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const { draft: d, tokens: t } = draftRef.current;
        const inReview = reviewSelection(reviewRef.current, h);
        if (inReview) {
          setBar({
            kind: "review",
            label: replacesPick(inReview) ? "Use only these words" : "Add these words",
            ...barPlacement(reviewWrapRef.current, inReview.rect),
          });
          return;
        }
        const inQuote = quoteSelection(quoteRef.current, t);
        if (inQuote) {
          setBar({
            kind: "quote",
            label: inQuote.indexes.every(i => d.selected.has(i)) ? "Remove highlight" : "Highlight these words",
            ...barPlacement(quoteWrapRef.current, inQuote.rect),
          });
          return;
        }
        setBar(null);
      }, SELECTION_SETTLE_MS);
    };
    // A press anywhere but the button hides it at once, so a new drag never starts on a stale one. A
    // mouse press also clears the old selection first: a press on selected words otherwise starts
    // dragging that text instead of a new selection.
    const onPress = (event: Event) => {
      if ((event.target as Element | null)?.closest?.("[data-selection-bar]")) return;
      setBar(null);
      if (event.type === "mousedown") {
        const selection = window.getSelection();
        if (selection && !selection.isCollapsed) selection.removeAllRanges();
      }
    };
    document.addEventListener("selectionchange", onSelectionChange);
    document.addEventListener("mousedown", onPress, true);
    document.addEventListener("touchstart", onPress, { capture: true, passive: true });
    return () => {
      clearTimeout(timer);
      document.removeEventListener("selectionchange", onSelectionChange);
      document.removeEventListener("mousedown", onPress, true);
      document.removeEventListener("touchstart", onPress, { capture: true } as EventListenerOptions);
    };
  }, [h]);

  const preventDrag = (event: React.DragEvent) => event.preventDefault();

  const listId = React.useId();
  const [activeSentence, setActiveSentence] = React.useState(-1);
  const [activeWord, setActiveWord] = React.useState(-1);
  const wordIndexes = tokens.map((token, i) => (token === ELLIPSIS ? -1 : i)).filter(i => i >= 0);

  // ---- footer ----

  const startOver = () => setDraft(draftFrom(stored.quote, stored.phrases));

  const done = () => {
    const next: QuoteEdit = {
      reviewId: Number(review.id),
      condensedQuote: sanitizeQuote(draft.quote),
      condensedHighlightPhrases: phrases,
    };
    if (canSaveToReview && saveToReview) {
      ReviewServices.updateReview(review.id, {
        condensedQuote: next.condensedQuote,
        condensedHighlightPhrases: next.condensedHighlightPhrases,
      })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["reviews"] });
          queryClient.invalidateQueries({ queryKey: ["review-details"] });
          queryClient.invalidateQueries({ queryKey: ["review", String(review.id)] });
        })
        .catch(() => toast.error(SAVE_TO_REVIEW_FAILED));
    }
    onDone(atStored ? null : next);
    onClose();
  };

  // ---- rendering ----

  const full = cover.filter(c => c === "on").length;
  const part = cover.filter(c => c === "part").length;

  const sentenceContent = (i: number) => {
    const span = spans[i];
    if (cover[i] !== "part") return h.slice(span.start, span.end);
    const pieces: React.ReactNode[] = [];
    let at = span.start;
    draft.ranges.forEach((range, k) => {
      const from = Math.max(range.start, span.start);
      const to = Math.min(range.end, span.end);
      if (to <= from) return;
      if (from > at) pieces.push(h.slice(at, from));
      pieces.push(
        <span
          key={k}
          className="box-decoration-clone bg-blue-100 text-blue-900 shadow-[inset_0_-3px_0_#1d4ed8]"
          data-quoted
        >
          {h.slice(from, to)}
        </span>
      );
      at = to;
    });
    if (at < span.end) pieces.push(h.slice(at, span.end));
    return pieces;
  };

  // No padding, borders or margins on the tappable text: a selection paints letters and spaces but never
  // padding, so padded items left white stripes in a drag. Boxes are drawn with shadows instead, and the
  // space between sentences is widened with word-spacing (still a space, so a drag paints across it) to
  // keep two picked sentences' boxes apart.
  const paragraph: React.ReactNode[] = [];
  spans.forEach((span, i) => {
    if (i > 0)
      paragraph.push(
        <span key={`gap${i}`} className="[word-spacing:6px]">
          {h.slice(spans[i - 1].end, span.start)}
        </span>
      );
    paragraph.push(
      <span
        key={i}
        id={`${listId}-s${i}`}
        role="option"
        aria-selected={cover[i] === "on"}
        data-coverage={cover[i] || "none"}
        className={cn(
          "box-decoration-clone cursor-pointer rounded-[3px] text-slate-700 underline decoration-slate-400 decoration-dotted underline-offset-4 transition-colors hover:bg-slate-200 hover:shadow-[0_0_0_1px_#e2e8f0]",
          cover[i] === "on" &&
            "bg-blue-100 text-blue-900 no-underline shadow-[0_0_0_1px_#dbeafe,0_0_0_3px_#1d4ed8] hover:bg-blue-100 hover:shadow-[0_0_0_1px_#dbeafe,0_0_0_3px_#1d4ed8]",
          cover[i] === "part" && "no-underline",
          i === activeSentence && ACTIVE_ITEM,
          refusal?.kind === "sentence" && refusal.index === i && REFUSED_ITEM
        )}
        onClick={() => {
          setActiveSentence(i);
          if (!selectingText()) toggleSentence(i);
        }}
      >
        {sentenceContent(i)}
      </span>
    );
  });

  const wordSpan = (token: string, i: number) =>
    token === ELLIPSIS ? (
      <span key={i} className="text-slate-400" aria-hidden="true">
        {token}
      </span>
    ) : (
      <span
        key={i}
        id={`${listId}-w${i}`}
        role="option"
        aria-selected={draft.selected.has(i)}
        className={cn(
          "cursor-pointer rounded-[3px] text-slate-800",
          draft.selected.has(i) ? "hover:bg-amber-200" : "hover:bg-slate-100",
          i === activeWord && ACTIVE_ITEM,
          refusal?.kind === "word" && refusal.index === i && REFUSED_ITEM
        )}
        onClick={() => {
          setActiveWord(i);
          if (!selectingText()) toggleWord(i);
        }}
      >
        {token}
      </span>
    );

  // A highlighted phrase is one run, as the image shows it: its words and the spaces between them share
  // one tint and underline. Each word stays tappable on its own.
  const quoteWords: React.ReactNode[] = [];
  let k = 0;
  while (k < tokens.length) {
    if (k > 0) quoteWords.push(" ");
    if (!draft.selected.has(k)) {
      quoteWords.push(wordSpan(tokens[k], k));
      k++;
      continue;
    }
    const run: React.ReactNode[] = [];
    const first = k;
    while (k < tokens.length && draft.selected.has(k)) {
      if (k > first) run.push(" ");
      run.push(wordSpan(tokens[k], k));
      k++;
    }
    quoteWords.push(
      <span
        key={`phrase${first}`}
        className="box-decoration-clone rounded-[4px] bg-amber-100 text-amber-900 shadow-[inset_0_-3px_0_#f59e0b]"
        data-phrase
      >
        {run}
      </span>
    );
  }

  const stepHead = (n: number, title: string) => (
    <div className="flex items-center gap-2.5">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-[13px] font-extrabold text-white">
        {n}
      </span>
      <h3 className="m-0 text-base font-extrabold text-slate-800">{title}</h3>
    </div>
  );

  return (
    <>
      <Dialog.Header>
        <Dialog.Title className="text-[17px] font-extrabold text-slate-800">Edit the quote</Dialog.Title>
      </Dialog.Header>
      <Dialog.Content className="min-h-0 overflow-y-auto">
        <div className="grid items-start gap-6 px-1 py-1 text-[15px] min-[760px]:grid-cols-2 min-[760px]:gap-7">
          <section className="flex flex-col gap-2">
            {stepHead(1, "Pick the sentences you want")}
            <p className="m-0 text-[13px] text-slate-500">
              Tap a sentence to add it to your quote. Tap again to take it out. To use part of a sentence, select just
              those words.
            </p>
            <div ref={reviewWrapRef} className="relative">
              <p
                ref={reviewRef}
                role="listbox"
                aria-multiselectable="true"
                aria-label="The review"
                tabIndex={0}
                aria-activedescendant={activeSentence >= 0 ? `${listId}-s${activeSentence}` : undefined}
                className={cn(
                  "group/list m-0 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-[16px] leading-[1.9] outline-none",
                  SELECTABLE
                )}
                onFocus={() => activeSentence < 0 && setActiveSentence(0)}
                onKeyDown={event =>
                  listKeyDown(
                    event,
                    spans.map((_, i) => i),
                    activeSentence,
                    setActiveSentence,
                    toggleSentence
                  )
                }
                onDragStart={preventDrag}
                data-testid="review-text"
              >
                {paragraph}
              </p>
              {bar?.kind === "review" && (
                <SelectionBar bar={bar} wrap={reviewWrapRef.current} onApply={applyReviewSelection} />
              )}
            </div>
            {draft.gapRefused ? (
              <Notice ref={gapMessageRef} tone="refused">
                {GAP_MESSAGE} <WhyLink onClick={() => setWhy("skips")} />
              </Notice>
            ) : (
              <p className="m-0 text-[13px] font-semibold text-slate-500" data-testid="picked-count">
                {pickedLine(full, part)}
              </p>
            )}
          </section>

          <section className="flex flex-col gap-2">
            {stepHead(2, "Tap words to make them stand out")}
            <p className="m-0 text-[13px] text-slate-500">
              Tap a word, or select a few, to make them stand out on the image. Two short phrases at most.
            </p>
            {tokens.length ? (
              <div ref={quoteWrapRef} className="relative">
                <div
                  ref={quoteRef}
                  role="listbox"
                  aria-multiselectable="true"
                  aria-label="Your quote"
                  tabIndex={0}
                  aria-activedescendant={activeWord >= 0 ? `${listId}-w${activeWord}` : undefined}
                  aria-invalid={tooLong || undefined}
                  aria-describedby={tooLong ? `${listId}-too-long` : undefined}
                  className={cn(
                    "group/list rounded-xl border border-slate-200 px-4 py-3.5 text-[17px] leading-[1.8] outline-none",
                    // A ring, not a thicker border, so the words don't move.
                    tooLong && "border-red-600 ring-2 ring-red-600",
                    SELECTABLE
                  )}
                  onFocus={() => !wordIndexes.includes(activeWord) && setActiveWord(wordIndexes[0] ?? -1)}
                  onKeyDown={event => listKeyDown(event, wordIndexes, activeWord, setActiveWord, toggleWord)}
                  onDragStart={preventDrag}
                  data-testid="quote-box"
                >
                  “{quoteWords}”
                </div>
                {bar?.kind === "quote" && (
                  <SelectionBar bar={bar} wrap={quoteWrapRef.current} onApply={applyQuoteSelection} />
                )}
              </div>
            ) : (
              <div className="rounded-xl border border-slate-200 px-4 py-3.5 text-[15px] text-slate-400">
                Your quote shows here once you pick a sentence.
              </div>
            )}
            {tooLong && (
              <Notice tone="error" id={`${listId}-too-long`}>
                {LENGTH_MESSAGE} <WhyLink onClick={() => setWhy("length")} />
              </Notice>
            )}
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <Button
                  variant="link"
                  size="inline"
                  className="text-[13px] font-bold"
                  disabled={draft.suggesting || !tokens.length || overLength}
                  onClick={suggest}
                >
                  <Sparkles className="size-3.5" aria-hidden="true" />
                  {draft.suggesting ? "Finding highlights…" : "Suggest highlights"}
                </Button>
                {/* The review's own long quote is allowed, so no error explains the disabled link; this does. */}
                {overLength && !tooLong && (
                  <span className="ml-2 text-[13px] text-slate-500">Shorten the quote to get suggestions.</span>
                )}
              </div>
              {/* Quiet and only there when needed: one tap instead of a tap per highlighted word. */}
              {draft.selected.size > 0 && !draft.suggesting && (
                <Button
                  variant="link"
                  size="inline"
                  className="shrink-0 text-[13px] font-semibold text-slate-500 hover:text-slate-700"
                  onClick={() => setSelected(new Set())}
                >
                  Clear highlights
                </Button>
              )}
            </div>
            {/* Room kept for one line, so a message appearing doesn't push the checkbox below it. */}
            <div className="min-h-5">
              {draft.phraseRefused ? (
                <Notice ref={phraseMessageRef} tone="refused">
                  {PHRASE_LIMIT_MESSAGE} <WhyLink onClick={() => setWhy("highlights")} />
                </Notice>
              ) : (
                draft.suggestFailed && <Notice tone="refused">{SUGGEST_FAILED}</Notice>
              )}
            </div>
          </section>

          {canSaveToReview && (
            <div className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-sm min-[760px]:col-span-2">
              <Checkbox
                className="mt-0.5"
                checked={saveToReview}
                onCheckedChange={checked => setSaveToReview(!!checked)}
                aria-labelledby="quote-save-to-review"
                aria-describedby="quote-save-to-review-help"
              />
              <span className="flex flex-col">
                <span
                  id="quote-save-to-review"
                  className="cursor-pointer font-bold text-slate-800"
                  onClick={() => setSaveToReview(v => !v)}
                >
                  Use this quote for this review from now on
                </span>
                <span id="quote-save-to-review-help" className="text-[13px] text-slate-500">
                  Updates the review in Reviews and your review widget. Posts already made don't change.
                </span>
              </span>
            </div>
          )}
        </div>
      </Dialog.Content>
      <Dialog.Footer className="sm:items-center sm:justify-between">
        <Button variant="link" className="px-0 font-bold" disabled={atStored} onClick={startOver}>
          Start over
        </Button>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
          {/* Done's reason sits beside it: the message that explains it can be a column away. */}
          {doneBlocked && (
            <p className="m-0 text-center text-xs text-slate-500 sm:text-right" data-testid="done-blocked-reason">
              {doneBlocked}
            </p>
          )}
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button className="font-bold" disabled={!!doneBlocked} onClick={done}>
            Done
          </Button>
        </div>
      </Dialog.Footer>
      <WhyDialog topic={why} onClose={() => setWhy(null)} />
    </>
  );
};

/** Picks the words of a review that a post's image shows, and the ones it makes stand out. */
export const QuoteEditorDialog: React.FC<QuoteEditorDialogProps> = props => (
  <Dialog
    open={!!props.review}
    onOpenChange={open => !open && props.onClose()}
    size="xl"
    showCloseButton
    className="max-h-[calc(100dvh-2rem)] grid-rows-[auto_minmax(0,1fr)_auto]"
  >
    {props.review && <EditorBody key={props.review.id} {...props} review={props.review} />}
  </Dialog>
);
